import { useState } from "react";
import { BasketItem, setBasketItemExercise } from "../../../models/BasketItem";
import { Assort } from "../../../models/Assort";
import useFacility from "../../../../utils/hooks/useFacility";
import getId from "../../../../utils/getId";
import { TicketAccess } from "../../../models/TicketAccess";
import { format } from "date-fns";

export default function usePreBasket() {
  const [preBasket, setPreBasket] = useState<BasketItem[]>([]);
  const facilityIds = useFacility();
  const isAssortInBasket = (assort: Assort) => preBasket.some((item) => item.assortIds === getId(assort));

  const findBasketItem = (assort: Assort) => preBasket.find((item) => item.assortIds === getId(assort));

  const checkQnt = (assort: Assort) => {
    const item = findBasketItem(assort);
    return item ? item.qnt : 0;
  };

  const validateRemove = (assort: Assort) => checkQnt(assort) > 1;

  const setBasketItem = (assort: Assort): BasketItem => {
    return {
      assortIds: getId(assort),
      qnt: 1,
      facilityIds,
      price: assort.listPrice,
      validFrom: assort.validFromDate ? format(assort.validFromDate, "yyyy-MM-dd") : null,
    } as BasketItem;
  };

  const getBasketItemExerciseByAssortMinSale = (assort: Assort, ticketAccess: TicketAccess) => {
    if (checkQnt(assort) >= assort.minSale) {
      return setBasketItemExercise(facilityIds, getId(assort), assort.price, [ticketAccess], null, 1);
    } else {
      return setBasketItemExercise(facilityIds, getId(assort), assort.price, [ticketAccess], null, assort.minSale);
    }
  };

  const handleAddToBasketWithoutTicketAccess = (assort: Assort) => {
    if (isAssortInBasket(assort)) {
      setPreBasket(preBasket.map((item) => (item.assortIds === getId(assort) ? { ...item, qnt: item.qnt + 1 } : item)));
    } else {
      setPreBasket([setBasketItem(assort), ...preBasket]);
    }
  };

  const handleAddToBasketWithTicketAccess = (assort: Assort, ticketAccess: TicketAccess) => {
    const assortId = getId(assort);
    const itemIndex = preBasket.findIndex(
      (item) => item.assortIds === assortId && item.ticketAccess.some((t) => t.exerciseIds === ticketAccess.exerciseIds)
    );

    if (itemIndex > -1) {
      setPreBasket((prevBasket) =>
        prevBasket.map((item, index) => (index === itemIndex ? { ...item, qnt: item.qnt + 1 } : item))
      );
    } else {
      setPreBasket([getBasketItemExerciseByAssortMinSale(assort, ticketAccess), ...preBasket]);
    }
  };

  const addItemToBasket = (assort: Assort, ticketAccess?: TicketAccess) => {
    // getExerciseByPassFlexi(assort.objectId);
    if (ticketAccess && ticketAccess?.exerciseIds) {
      handleAddToBasketWithTicketAccess(assort, ticketAccess);
    } else {
      handleAddToBasketWithoutTicketAccess(assort);
    }
  };

  const handleRemoveItemWithoutTicketAccess = (assort: Assort) => {
    if (validateRemove(assort)) {
      if (checkQnt(assort) === assort.minSale) {
        setPreBasket(preBasket.filter((item) => item.assortIds !== getId(assort)));
      } else {
        setPreBasket(
          preBasket.map((item) => (item.assortIds === getId(assort) ? { ...item, qnt: item.qnt - 1 } : item))
        );
      }
    } else {
      setPreBasket(preBasket.filter((item) => item.assortIds !== getId(assort)));
    }
  };

  const handleRemoveItemWithWithTicketAccess = (assort: Assort, ticketAccess: TicketAccess) => {
    const assortId = getId(assort);
    const isTargetItem = (item: BasketItem) =>
      item.assortIds === assortId &&
      item.ticketAccess.some((ticketAccessItem) => ticketAccessItem.exerciseIds === ticketAccess.exerciseIds);

    const updatedPreBasket = preBasket.reduce<BasketItem[]>((acc, item) => {
      if (isTargetItem(item)) {
        // Sprawdzamy ilość i decydujemy, czy zmniejszamy `qnt`, czy usuwamy element
        if (item.qnt > assort.minSale) {
          if (assort.minSale === item.qnt) {
            acc.push({ ...item, qnt: item.qnt - assort.minSale });
          } else {
            acc.push({ ...item, qnt: item.qnt - 1 });
          }
        }
        // Jeśli `qnt` wynosi 1, element nie zostanie dodany do `acc`, więc zostanie usunięty
      } else {
        // Dodajemy pozostałe elementy bez zmian
        acc.push(item);
      }
      return acc;
    }, []);

    setPreBasket(updatedPreBasket);
  };

  const removeItemFromBasket = (assort: Assort, ticketAccess?: TicketAccess) => {
    if (ticketAccess && ticketAccess.exerciseIds) {
      handleRemoveItemWithWithTicketAccess(assort, ticketAccess);
    } else {
      handleRemoveItemWithoutTicketAccess(assort);
    }
  };

  const clearBasket = () => {
    setPreBasket([]);
  };

  return {
    preBasket,
    addItemToBasket,
    removeItemFromBasket,
    clearBasket,
  };
}
